/**
 * Intake Dashboard Page
 * 
 * Comprehensive dashboard for managing patient intakes
 * with tabs, filters, metrics, and quick actions
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  FileText, 
  CheckCircle2, 
  Clock, 
  AlertTriangle, 
  XCircle,
  Ban,
  Filter,
  Search,
  Download,
  RefreshCw,
  Mail,
  BarChart3,
  Users,
  TrendingUp,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Send,
  Eye,
  Loader2,
  Bell,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { 
  getDashboardData, 
  getIntakeMetrics,
  getRecentCompletions,
  resendIntakeEmail,
  cancelIntakeRequest,
  downloadResponsePDF,
  PendingIntake,
  getTherapyCategoryLabel,
  getIntakeTypeLabel,
  RecentCompletion,
} from '@/services/intakeService';
import { IntakeMetrics } from '@/components/clinic/IntakeMetrics';
import { IntakeTable } from '@/components/clinic/IntakeTable';
import { IntakeAnalytics } from '@/components/clinic/IntakeAnalytics';
import IntakeResponseReviewDrawer from '@/components/clinic/IntakeResponseReviewDrawer';
import { useClinic } from '@/contexts/ClinicContext';
import { DatePicker } from '@/components/ui/date-picker';
import { getDateRange, DATE_RANGE_OPTIONS, type DateRangePreset } from '@/lib/dateRanges';

// ============================================================================
// TYPES
// ============================================================================

type TabStatus = 'all' | 'pending' | 'started' | 'completed' | 'reviewed' | 'expired' | 'cancelled';

interface Filters {
  search: string;
  therapyCategory: string;
  dateRangePreset: DateRangePreset;
  customStartDate?: Date;
  customEndDate?: Date;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const IntakeDashboard: React.FC = () => {
  const { clinic } = useClinic();
  const isGuided = clinic?.clinic_archetype === 'guided';
  const formLabel = isGuided ? 'enrollment' : 'intake';
  const formLabelCap = isGuided ? 'Enrollment' : 'Intake';
  const [activeTab, setActiveTab] = useState<TabStatus>('all');
  const [intakes, setIntakes] = useState<PendingIntake[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [metrics, setMetrics] = useState<any>(null);
  const [recentCompletions, setRecentCompletions] = useState<RecentCompletion[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<Omit<Filters, 'search'>>({
    therapyCategory: 'all',
    dateRangePreset: 'this_year',
  });
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [reviewDrawerOpen, setReviewDrawerOpen] = useState(false);
  const [selectedResponseId, setSelectedResponseId] = useState<string | null>(null);
  const { toast } = useToast();

  // Load data
  const loadData = useCallback(async (showRefresh = false) => {
    if (showRefresh) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }

    try {
      // Calculate date range
      let startDate: string;
      let endDate: string;
      if (filters.dateRangePreset === 'custom' && filters.customStartDate && filters.customEndDate) {
        startDate = filters.customStartDate.toISOString();
        endDate = filters.customEndDate.toISOString();
      } else if (filters.dateRangePreset !== 'custom') {
        const range = getDateRange(filters.dateRangePreset);
        startDate = range.startDate;
        endDate = range.endDate;
      } else {
        // Custom selected but dates not yet picked - use this month as fallback
        const range = getDateRange('this_month');
        startDate = range.startDate;
        endDate = range.endDate;
      }

      // Load dashboard data, metrics, and recent completions in parallel
      const [dashboardResult, metricsResult, completionsResult] = await Promise.all([
        getDashboardData({
          status: activeTab === 'all' ? undefined : activeTab,
          therapyCategory: filters.therapyCategory === 'all' ? undefined : filters.therapyCategory,
          startDate,
          endDate,
          page: pagination.page,
          limit: pagination.limit,
          clinicId: clinic?.id,
        }),
        getIntakeMetrics(startDate, endDate, clinic?.id),
        getRecentCompletions(60),
      ]);

      if (dashboardResult.success && dashboardResult.data) {
        setIntakes(dashboardResult.data.intakes);
        setPagination(prev => ({
          ...prev,
          ...dashboardResult.data?.pagination,
        }));
      }

      if (metricsResult.success && metricsResult.data) {
        setMetrics(metricsResult.data);
      }

      if (completionsResult.success && completionsResult.data) {
        setRecentCompletions(completionsResult.data);
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load dashboard data',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [activeTab, filters, pagination.page, pagination.limit, toast, clinic?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Poll for recent completions every 30 seconds
  // useEffect(() => {
  //   const interval = setInterval(async () => {
  //     const result = await getRecentCompletions(5);
  //     if (result.success && result.data && result.data.length > 0) {
  //       // Show notification for new completions
  //       result.data.forEach(completion => {
  //         toast({
  //           title: '✅ Intake Completed',
  //           description: `${completion.patientName} has completed their ${getTherapyCategoryLabel(completion.therapyCategory)} intake.`,
  //         });
  //       });
  //       loadData(true);
  //     }
  //   }, 30000);

  //   return () => clearInterval(interval);
  // }, [toast, loadData]);

  // Handle resend email
  const handleResend = async (intakeId: string) => {
    try {
      const result = await resendIntakeEmail(intakeId);
      if (result.success) {
        toast({
          title: 'Email Sent',
          description: `${formLabelCap} email has been resent to the patient.`,
        });
        loadData(true);
      } else {
        toast({
          title: 'Failed to Resend',
          description: result.message || 'Could not resend the email.',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      toast({
        title: 'Failed to Resend',
        description: error?.message || 'An error occurred while resending the email.',
        variant: 'destructive',
      });
    }
  };

  // Handle cancel intake
  const handleCancel = async (intakeId: string) => {
    try {
      const result = await cancelIntakeRequest(intakeId);
      if (result.success) {
        toast({
          title: `${formLabelCap} Cancelled`,
          description: `The ${formLabel} request has been cancelled.`,
        });
        loadData(true);
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: `Failed to cancel ${formLabel}`,
        variant: 'destructive',
      });
    }
  };

  // Handle view response
  const handleViewResponse = (responseId: string) => {
    setSelectedResponseId(responseId);
    setReviewDrawerOpen(true);
  };

  // Handle download PDF
  const handleDownloadPDF = async (responseId: string) => {
    try {
      await downloadResponsePDF(responseId);
      toast({
        title: 'Download Started',
        description: 'Your PDF is being downloaded.',
        variant: 'success',
      });
    } catch (error) {
      toast({
        title: 'Download Failed',
        description: 'Failed to download PDF. Please try again.',
        variant: 'destructive',
      });
    }
  };

  // Handle after response is reviewed
  const handleResponseReviewed = () => {
    loadData(true); // Refresh the intakes list
  };

  // Filter intakes by search
  const filteredIntakes = intakes.filter(intake => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    const patientName = intake.patient_name || intake.patientName || '';
    const patientEmail = intake.patient_email || intake.patientEmail || '';
    return (
      patientName.toLowerCase().includes(search) ||
      patientEmail.toLowerCase().includes(search)
    );
  });

  // Get tab counts
  const getTabCount = (status: TabStatus): number => {
    if (!metrics) return 0;
    if (status === 'all') return metrics.total;
    return metrics[status] || 0;
  };

  return (
    <div className="flex flex-col h-full">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h1 className="font-montserrat text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">
            {formLabelCap} Dashboard
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Manage patient {formLabel} requests and track completion status
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAnalytics(!showAnalytics)}
            className="h-9"
          >
            <BarChart3 className="w-3.5 h-3.5 mr-1.5" />
            {showAnalytics ? 'Hide' : 'Show'} Analytics
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => loadData(true)}
            disabled={isRefreshing}
            className="h-9"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

        {/* Recent Completions Alert */}
        {recentCompletions.length > 0 && (
          <div className="mb-4 p-3 rounded-md bg-brand-teal/10 border border-brand-teal/20">
            <div className="flex items-center gap-2 mb-2">
              <Bell className="w-4 h-4 text-brand-teal" />
              <span className="text-sm font-semibold text-slate-900">Recent Completions</span>
              <Badge className="bg-brand-teal text-white text-[10px] px-1.5 py-0">
                {recentCompletions.length} new
              </Badge>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {recentCompletions.slice(0, 5).map(completion => (
                <Badge 
                  key={completion.id} 
                  variant="outline" 
                  className="bg-white border-slate-200 text-slate-700 text-[10px] px-2 py-0.5"
                >
                  {completion.patientName} - {getTherapyCategoryLabel(completion.therapyCategory)}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Metrics Cards */}
        <div className="mb-4">
          <IntakeMetrics metrics={metrics} isLoading={isLoading} />
        </div>

        {/* Analytics Section */}
        {showAnalytics && (
          <div className="mb-4">
            <IntakeAnalytics clinicId={clinic?.id} />
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <Input
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value.trimStart())}
              className="pl-9 h-9 text-sm"
            />
          </div>
          
          <Select 
            value={filters.therapyCategory} 
            onValueChange={(value) => setFilters(prev => ({ ...prev, therapyCategory: value }))}
          >
            <SelectTrigger className="w-[180px] h-9 text-sm">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              <SelectItem value="trt">Men's Health (TRT)</SelectItem>
              <SelectItem value="hrt">Women's Health (HRT)</SelectItem>
              <SelectItem value="glp1">Weight Management</SelectItem>
              <SelectItem value="peptide">Peptide Therapy</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={filters.dateRangePreset}
            onValueChange={(value) => setFilters(prev => ({ ...prev, dateRangePreset: value as DateRangePreset }))}
          >
            <SelectTrigger className="w-[150px] h-9 text-sm">
              <SelectValue placeholder="Date Range" />
            </SelectTrigger>
            <SelectContent>
              {DATE_RANGE_OPTIONS.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {filters.dateRangePreset === 'custom' && (
            <div className="flex items-center gap-2">
              <DatePicker
                value={filters.customStartDate}
                onValueChange={(date) => setFilters(prev => ({ ...prev, customStartDate: date }))}
                placeholder="Start date"
                maxDate={filters.customEndDate || new Date()}
                className="w-[140px]"
                inputClassName="h-9 text-sm"
              />
              <span className="text-sm text-slate-400">to</span>
              <DatePicker
                value={filters.customEndDate}
                onValueChange={(date) => setFilters(prev => ({ ...prev, customEndDate: date }))}
                placeholder="End date"
                minDate={filters.customStartDate}
                maxDate={new Date()}
                className="w-[140px]"
                inputClassName="h-9 text-sm"
              />
            </div>
          )}
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TabStatus)}>
          <TabsList className="mb-4">
            <TabsTrigger 
              value="all" 
              className="data-[state=active]:bg-brand-teal data-[state=active]:text-white text-xs"
            >
              All ({getTabCount('all')})
            </TabsTrigger>
            <TabsTrigger 
              value="pending" 
              className="data-[state=active]:bg-amber-500 data-[state=active]:text-white text-xs"
            >
              <Clock className="w-3 h-3 mr-1" />
              Pending ({getTabCount('pending')})
            </TabsTrigger>
            <TabsTrigger 
              value="started" 
              className="data-[state=active]:bg-blue-500 data-[state=active]:text-white text-xs"
            >
              <RefreshCw className="w-3 h-3 mr-1" />
              In Progress ({getTabCount('started')})
            </TabsTrigger>
            <TabsTrigger 
              value="completed" 
              className="data-[state=active]:bg-brand-teal data-[state=active]:text-white text-xs"
            >
              <CheckCircle2 className="w-3 h-3 mr-1" />
              Completed ({getTabCount('completed')})
            </TabsTrigger>
            <TabsTrigger 
              value="reviewed" 
              className="data-[state=active]:bg-indigo-500 data-[state=active]:text-white text-xs"
            >
              <CheckCircle2 className="w-3 h-3 mr-1" />
              Reviewed ({getTabCount('reviewed')})
            </TabsTrigger>
            <TabsTrigger 
              value="expired" 
              className="data-[state=active]:bg-red-500 data-[state=active]:text-white text-xs"
            >
              <XCircle className="w-3 h-3 mr-1" />
              Expired ({getTabCount('expired')})
            </TabsTrigger>
            <TabsTrigger 
              value="cancelled" 
              className="data-[state=active]:bg-slate-500 data-[state=active]:text-white text-xs"
            >
              <Ban className="w-3 h-3 mr-1" />
              Cancelled ({getTabCount('cancelled')})
            </TabsTrigger>
          </TabsList>

          {/* Table Content */}
          <TabsContent value={activeTab} className="mt-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 animate-spin text-brand-teal" />
              </div>
            ) : (
              <>
                <IntakeTable 
                  intakes={filteredIntakes}
                  onResend={handleResend}
                  onCancel={handleCancel}
                  onView={handleViewResponse}
                  onDownload={handleDownloadPDF}
                />

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4">
                    <p className="text-xs text-slate-500">
                      Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
                      {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                      {pagination.total} results
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={pagination.page === 1}
                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                        className="h-8 w-8 p-0"
                      >
                        <ChevronLeft className="w-3.5 h-3.5" />
                      </Button>
                      <span className="text-xs text-slate-600">
                        Page {pagination.page} of {pagination.totalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={pagination.page === pagination.totalPages}
                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                        className="h-8 w-8 p-0"
                      >
                        <ChevronRight className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </TabsContent>
        </Tabs>

        {/* Response Review Drawer */}
        <IntakeResponseReviewDrawer
          open={reviewDrawerOpen}
          onOpenChange={setReviewDrawerOpen}
          responseId={selectedResponseId}
          onReviewed={handleResponseReviewed}
          onRunIntelliHealth={() => {
            // Could navigate to IntelliHealth page or show analytics
            console.log('Run IntelliHealth analysis');
          }}
        />
      </div>
    );
  };

export default IntakeDashboard;
